import type { AxiosInstance, AxiosProgressEvent } from "axios";
import axios from "axios";
import { useClient } from "../state/client";
import { initialized } from "../utils/initializedUtil";
import { API_BASE_URL } from "../constants";
import { isEmpty, isNil } from "lodash-es";
import { toCustomerResourcePage } from "../utils/mappers/customerResource";
import mime from "mime";
import { useQuery, useQueryClient } from "@tanstack/solid-query";
import { minutes, seconds } from "../utils/timeUtil";
import logger from "../utils/logger";
import type CustomerId from "../state/CustomerId";

export const PAGE_SIZE = 6;
export const THUMBNAIL_SIZE = 400;
export const RESOURCE_EXISTS_QUERY_KEY = "resourceExists";

let client: AxiosInstance | null = null;
let customerId: CustomerId | null = null;

export type UploadCustomerResourceDto = {
  id: string;
  name: string;
  contentType: string;
};

export type SecureUriDto = {
  contentType: string;
  secureUri: string;
};

export type CustomerResourceDto = {
  id: string;
  length: number;
  meta: {
    contentType: string;
    name: string;
  };
  secureUri: string;
};

export type CustomerResourceMetaDto = {
  id: string;
  customerId: number;
  contentType: string;
  name: string;
  shared: boolean;
};

export type CustomerResourcePageDto = {
  content: CustomerResourceDto[];
  totalElements: number;
  totalPages: number;
  number: number;
};

export type PendingCustomerResource = Omit<CustomerResource, "id">;

export type CustomerResourcePage = {
  content: CustomerResource[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
};

export type CustomerResource = {
  id: string;
  name: string;
  fileName: string;
  fileSize: string;
  extension: string;
  source: string;
  sourceThumbnail?: string;
};

const init = (cusId: CustomerId): void => {
  customerId = cusId;
  client = useClient();
};

const getClient = () => initialized(client, "client");

const getSecureUri = async (resourceId: string, maxWidth?: number) => {
  const { data } = await getClient().get<SecureUriDto>(`/api/v1.0/customers/${customerId}/resources/${resourceId}/uri`);
  return `${API_BASE_URL}/api/v1.0/downloads/customers/${customerId}/resources${!isNil(maxWidth) ? `/thumbnail/${maxWidth}` : ""}?${data.secureUri}`;
};
const getSecureUriQueryOptions = (resourceId: string, maxWidth?: number) => ({
  queryKey: [resourceId, maxWidth],
  queryFn: async () => getSecureUri(resourceId, maxWidth),
  staleTime: minutes(25), // Link is valid for 30 minutes, make stale just before
});

const getResourceMeta = async (resourceId: string) => {
  const { data } = await getClient().get<CustomerResourceMetaDto>(
    `/api/v1.0/customers/${customerId}/resources/${resourceId}`,
  );
  return data;
};

const CustomerResourceService = {
  exists: async (resourceId: string) =>
    useQueryClient().ensureQueryData({
      queryKey: [RESOURCE_EXISTS_QUERY_KEY, resourceId],
      queryFn: async () => {
        try {
          const data = await getResourceMeta(resourceId);
          return !isNil(data);
        } catch (e) {
          logger.debug("Resource does not exist:", resourceId, e);
          return false;
        }
      },
      staleTime: Infinity, // use cached data for entire session
    }),
  getSecureUri: (resourceId: string, maxWidth?: number) =>
    useQueryClient().ensureQueryData(getSecureUriQueryOptions(resourceId, maxWidth)),
  getSecureUriQuery: (resourceId: string, maxWidth?: number) =>
    useQuery(() => getSecureUriQueryOptions(resourceId, maxWidth)),
  getPublicUri: (resourceId: string) =>
    `${API_BASE_URL}/api/v1.0/common/customers/${customerId}/resources/${resourceId}`,
  upload: async (
    file: PendingCustomerResource,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    abortSignal?: AbortSignal,
  ): Promise<CustomerResource> => {
    const formData = new FormData();
    const res = await axios.get(file.source, { responseType: "blob" });
    const blob = await res.data;

    formData.append("file", blob, file.fileName);
    formData.append("name", file.name);
    formData.append("shared", "false"); // TODO: DEV-8308 - Remove this once sharedOnly is removed from the API
    formData.append("importFormat", "PNG");

    const { data } = await getClient().post<UploadCustomerResourceDto>(
      `/api/v1.0/customers/${customerId}/resources/upload`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
        signal: abortSignal,
        timeout: seconds(90),
      },
    );
    const id = data.id;
    const extension = mime.getExtension(data.contentType) ?? file.extension;
    if (file.extension === "pdf") {
      // PDFs can't be loaded into an img tag, fetch render URL
      return { ...file, id, extension, source: await getSecureUri(id) };
    }
    return { ...file, id, extension };
  },
  fetchPage: async (
    page: number,
    options?: {
      query?: string;
      pageSize?: number;
      thumbnailSize?: number;
    },
  ) => {
    const { query, pageSize = PAGE_SIZE, thumbnailSize = THUMBNAIL_SIZE } = options ?? {};
    const url = `/api/v1.0/customers/${customerId}/resources/page`;

    const params = new URLSearchParams();
    params.set("page", page.toString());
    params.set("size", pageSize.toString());
    if (!isNil(query) && !isEmpty(query)) {
      params.set("filter", query); // Automatically handles encoding
    }

    const { data } = await getClient().get<CustomerResourcePageDto>(`${url}?${params.toString()}`);
    return toCustomerResourcePage(customerId!, data, thumbnailSize);
  },
  delete: async (resourceId: string) => {
    await getClient().delete(`/api/v1.0/customers/${customerId}/resources/${resourceId}`);
  },
  init: init,
};

export default CustomerResourceService;
